import React, { useCallback, useEffect, useState } from 'react';
import Modal from './Modal';
import Icon from './icons';
import { apiGet, apiPost } from '../lib/api';
import { canManage } from '../lib/roles';

/**
 * Seamless Add Skill — URL / manual / one-click from suggested list.
 */
export default function AddSkill({ onImported }) {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('url'); // url | manual | suggested
  const [url, setUrl] = useState('');
  const [form, setForm] = useState({
    key: '',
    name: '',
    description: '',
    example_prompt: '',
  });
  const [enableNow, setEnableNow] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [suggested, setSuggested] = useState({ curated: [], anthropic: [], anthropic_repo: [], github: [] });
  const [suggestedError, setSuggestedError] = useState('');

  const loadSuggested = useCallback(async () => {
    try {
      setSuggestedError('');
      const res = await apiGet('/skills/suggested');
      setSuggested({
        curated: res.curated || [],
        anthropic: res.anthropic || [],
        anthropic_repo: res.anthropic_repo || [],
        github: res.github || [],
      });
    } catch (e) {
      const msg = String(e.message || e);
      console.warn('[AddSkill] suggested failed', e);
      setSuggestedError(msg);
      setSuggested({ curated: [], anthropic: [], anthropic_repo: [], github: [] });
    }
  }, []);

  useEffect(() => {
    if (open && tab === 'suggested') loadSuggested();
  }, [open, tab, loadSuggested]);

  if (!canManage()) return null;

  function reset() {
    setUrl('');
    setForm({ key: '', name: '', description: '', example_prompt: '' });
    setError('');
    setResult(null);
    setEnableNow(true);
  }

  function close() {
    setOpen(false);
    reset();
  }

  async function importPayload(body) {
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const res = await apiPost('/skills/import', { ...body, enable: enableNow });
      setResult(res);
      if (onImported) onImported();
      if (tab === 'suggested') loadSuggested();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }

  async function submitUrl(e) {
    e.preventDefault();
    if (!url.trim()) return;
    await importPayload({ url: url.trim() });
  }

  async function submitManual(e) {
    e.preventDefault();
    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }
    await importPayload({
      key: form.key.trim() || undefined,
      name: form.name.trim(),
      description: form.description.trim(),
      example_prompt: form.example_prompt.trim(),
    });
  }

  async function syncCatalog() {
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const res = await apiPost('/skills/catalog/sync', {});
      setResult({
        created: false,
        skill: { key: 'catalog_sync' },
        enabled: false,
        already_existed: true,
        warning: res.ok
          ? res.message || `Synced: +${res.anthropic?.created_count || 0} new, ${res.anthropic?.updated_count || 0} updated`
          : res.anthropic?.error || res.message || 'sync failed',
      });
      await loadSuggested();
      if (onImported) onImported();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }

  async function importAnthropicRepo() {
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const res = await apiPost('/skills/import-anthropic-repo', {});
      setResult({
        created: false,
        skill: { key: 'anthropic_repo_import' },
        enabled: false,
        already_existed: true,
        warning: res.ok
          ? res.message
          : res.message || 'import failed',
      });
      await loadSuggested();
      if (onImported) onImported();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }

  async function installSuggested(item) {
    setBusy(true);
    setError('');
    setResult(null);
    try {
      if (item.installed && item.installed_skill_id) {
        if (item.installed_lifecycle !== 'enabled') {
          const res = await apiPost(`/skills/${item.installed_skill_id}/enable`, {});
          setResult({ skill: res.skill, enabled: true, created: false, already_existed: true });
          if (onImported) onImported();
        } else {
          setResult({
            skill: { id: item.installed_skill_id, key: item.key, name: item.name },
            enabled: true,
            created: false,
            already_existed: true,
          });
        }
      } else if (item.url) {
        await importPayload({ url: item.url, key: item.key, name: item.name, description: item.description });
      } else {
        await importPayload({
          key: item.key,
          name: item.name,
          description: item.description,
        });
      }
      loadSuggested();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }

  const allSuggested = [
    ...(suggested.anthropic || []),
    ...(suggested.anthropic_repo || []),
    ...(suggested.curated || []),
    ...(suggested.github || []),
  ];

  return (
    <>
      <button type="button" className="btn-primary" onClick={() => setOpen(true)}>
        <Icon name="upload" /> Add skill
      </button>
      {open && (
        <Modal
          title="Add skill"
          onClose={close}
          wide
          footer={
            tab === 'suggested' ? (
              <>
                <button type="button" className="btn-secondary" onClick={importAnthropicRepo} disabled={busy}>
                  {busy ? 'Importing…' : 'Import github.com/anthropics/skills'}
                </button>
                <button type="button" className="btn-secondary" onClick={syncCatalog} disabled={busy}>
                  {busy ? 'Syncing…' : 'Sync Anthropic catalog'}
                </button>
                <button type="button" className="btn-secondary" onClick={close}>Close</button>
              </>
            ) : (
              <>
                <button type="button" className="btn-secondary" onClick={close}>Cancel</button>
                <button
                  type="submit"
                  form={tab === 'url' ? 'add-skill-url' : 'add-skill-manual'}
                  className="btn-primary"
                  disabled={busy}
                >
                  {busy ? 'Installing…' : enableNow ? 'Install & enable' : 'Install as draft'}
                </button>
              </>
            )
          }
        >
          <div className="add-skill__tabs">
            {[
              ['url', 'From URL'],
              ['manual', 'Manual'],
              ['suggested', 'Suggested'],
            ].map(([id, label]) => (
              <button
                key={id}
                type="button"
                className={`add-skill__tab ${tab === id ? 'is-active' : ''}`}
                onClick={() => { setTab(id); setError(''); setResult(null); }}
              >
                {label}
              </button>
            ))}
          </div>

          <label className="add-skill__enable">
            <input
              type="checkbox"
              checked={enableNow}
              onChange={(e) => setEnableNow(e.target.checked)}
            />
            Enable immediately (skip draft queue — seamless path)
          </label>

          {tab === 'url' && (
            <form id="add-skill-url" onSubmit={submitUrl}>
              <p className="bulk-import__hint">
                Paste a GitHub repo or SKILL.md URL. We fetch what we can and register the skill.
              </p>
              <input
                className="field-input"
                style={{ width: '100%', maxWidth: 'none', margin: 0 }}
                placeholder="https://github.com/org/repo or …/SKILL.md"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                required
              />
            </form>
          )}

          {tab === 'manual' && (
            <form id="add-skill-manual" className="add-skill__manual" onSubmit={submitManual}>
              <p className="bulk-import__hint">Name + description is enough. Key is auto-slugged if blank.</p>
              <label className="field-label">
                Name
                <input
                  className="field-input"
                  style={{ width: '100%', maxWidth: 'none' }}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                />
              </label>
              <label className="field-label">
                Key (optional)
                <input
                  className="field-input"
                  style={{ width: '100%', maxWidth: 'none' }}
                  value={form.key}
                  onChange={(e) => setForm({ ...form, key: e.target.value })}
                  placeholder="mkt_my_skill"
                />
              </label>
              <label className="field-label field-label--full">
                Description
                <textarea
                  className="field-input"
                  style={{ width: '100%', maxWidth: 'none' }}
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </label>
              <label className="field-label field-label--full">
                Example prompt
                <textarea
                  className="field-input"
                  style={{ width: '100%', maxWidth: 'none' }}
                  rows={2}
                  value={form.example_prompt}
                  onChange={(e) => setForm({ ...form, example_prompt: e.target.value })}
                />
              </label>
            </form>
          )}

          {tab === 'suggested' && (
            <div className="add-skill__suggested">
              <p className="bulk-import__hint">
                Hit <strong>Import github.com/anthropics/skills</strong> for Anthropic&apos;s public example
                skills (skill-creator, mcp-builder, webapp-testing, etc.) or <strong>Sync Anthropic catalog</strong>
                for the account Skills API (pptx/xlsx/docx/pdf). Curated list is always available. You still click
                Install/Enable.
              </p>
              {suggestedError && (
                <p className="status status--error">
                  Suggested API failed: {suggestedError}. If UI points at live, deploy this build or set
                  PUBLIC_API_URL=http://localhost:3000 for local.
                </p>
              )}
              {!suggestedError && (suggested.anthropic || []).length === 0 && (
                <p className="status status--muted">
                  No Anthropic-synced skills yet — use <strong>Sync Anthropic catalog</strong> below.
                </p>
              )}
              {allSuggested.length === 0 && !suggestedError ? (
                <p className="status status--muted">No suggestions loaded.</p>
              ) : (
                <ul className="add-skill__list">
                  {allSuggested.map((item) => (
                    <li key={`${item.source}-${item.key}`}>
                      <div>
                        <strong>{item.name}</strong>
                        <span className="add-skill__badge">{item.source}</span>
                        {item.license && (
                          <span className="add-skill__badge" title="License of the source SKILL.md">
                            {item.license}
                          </span>
                        )}
                        {item.installed && (
                          <span className="add-skill__badge add-skill__badge--ok">
                            {item.installed_lifecycle || 'installed'}
                          </span>
                        )}
                        <p>{item.description}</p>
                      </div>
                      <button
                        type="button"
                        className="btn-primary btn-small"
                        disabled={busy || (item.installed && item.installed_lifecycle === 'enabled')}
                        onClick={() => installSuggested(item)}
                      >
                        {item.installed && item.installed_lifecycle === 'enabled'
                          ? 'Enabled'
                          : item.installed
                            ? 'Enable'
                            : 'Install'}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {error && <div className="status status--error" style={{ marginTop: '0.75rem' }}>{error}</div>}
          {result && (
            <div className="bulk-import__summary" style={{ marginTop: '0.75rem' }}>
              {result.created === false && result.already_existed
                ? <>Already in registry: <strong>{result.skill?.key}</strong>
                  {result.enabled ? ' — enabled.' : '.'}</>
                : <>Installed <strong>{result.skill?.key || result.parsed?.key}</strong>
                  {result.enabled ? ' and enabled.' : ' as draft.'}</>}
              {result.warning && <div>Note: {result.warning}</div>}
            </div>
          )}
        </Modal>
      )}
    </>
  );
}
